/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { Box, css as cssCui } from '@coursera/coursera-ui';

import type { InformationCardProps } from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCard';
import InformationCard from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCard';
import InformationCardBody from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCardBody';
import { objectivesInformationCardStyles } from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/styles';
import SvgObjectivesStep1 from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/svg/SvgObjectivesStep1';
import SvgObjectivesStep2 from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/svg/SvgObjectivesStep2';
import SvgObjectivesStep3 from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/svg/SvgObjectivesStep3';
import {
  CardTitleH1,
  SkillCaption,
} from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/typography';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type ObjectivesInformationCardProps = Omit<InformationCardProps, 'trackingName' | 'trackingData'>;

const ObjectivesInformationCard = (props: ObjectivesInformationCardProps) => (
  <InformationCard {...props} trackingName="objectives_information_card">
    <InformationCardBody isFullBleed>
      <div {...cssCui(objectivesInformationCardStyles.cardHeader)}>
        <CardTitleH1>{_t('How SkillSets work')}</CardTitleH1>
      </div>
      <Box justifyContent="between" rootClassName={objectivesInformationCardStyles.container}>
        <Box
          flex={0}
          flexDirection="column"
          alignContent="center"
          justifyContent="start"
          rootClassName={objectivesInformationCardStyles.column}
        >
          <SvgObjectivesStep1 />

          <div>
            <SkillCaption>{_t('1. Find an objective')}</SkillCaption>
            <Typography2
              component="p"
              css={css`
                margin-bottom: var(--cds-spacing-150);
              `}
            >
              {_t('Your organizations defined the skill development objectives in this page.')}
            </Typography2>
            <Typography2
              component="p"
              css={css`
                margin-bottom: var(--cds-spacing-150);
              `}
            >
              {_t('Find an objective that is compatible with your current role and future career goals.')}
            </Typography2>
          </div>
        </Box>
        <Box
          flex={0}
          flexDirection="column"
          justifyContent="start"
          rootClassName={objectivesInformationCardStyles.column}
        >
          <SvgObjectivesStep2 />
          <div>
            <SkillCaption>{_t('2. Start learning')}</SkillCaption>
            <Typography2
              component="p"
              css={css`
                margin-bottom: var(--cds-spacing-150);
              `}
            >
              {_t('View skill-based recommendations and enroll in courses to start developing the skills you need.')}
            </Typography2>
            <Typography2
              component="p"
              css={css`
                margin-bottom: var(--cds-spacing-150);
              `}
            >
              {_t('Increase your skill proficiency by passing graded assessments.')}
            </Typography2>
          </div>
        </Box>
        <Box
          flex={0}
          flexDirection="column"
          justifyContent="start"
          rootClassName={objectivesInformationCardStyles.column}
        >
          <SvgObjectivesStep3 />
          <div>
            <SkillCaption>{_t('3. Track your progress')}</SkillCaption>
            <Typography2
              component="p"
              css={css`
                margin-bottom: var(--cds-spacing-150);
              `}
            >
              {_t('Compare your skill proficiency scores with the target proficiency scores for that objective.')}
            </Typography2>
          </div>
        </Box>
      </Box>
    </InformationCardBody>
  </InformationCard>
);

export default ObjectivesInformationCard;
